import http from "../../shared/mediaHttp";
import Vue from "vue";
import { uniqBy } from "lodash";

function base(libraryUUID) {
  if (!libraryUUID) throw new Error("Library UUID not set");
  return `dms/documents/${libraryUUID}`;
}

const documents = {
  state: () => ({
    libraryUUID: null,
    items: [],
    deleted: [],
    document: null,
  }),

  actions: {
    setLibrary({ commit }, uuid) {
      commit("SET_LIBRARY_UUID", uuid);
    },

    async createDocument({ commit }, { path, content, variant, sources, fields }) {
      const { data } = await http.post(`/v2/dms/documents/document`, {
        path,
        content,
        variant,
        sources,
        fields,
      });
      const { status } = data;
      commit("ADD_DOCUMENT", { name: `/${path}` });
      return data;
    },

    async fetchDocument({ commit, state }, { path }) {
      const { data } = await http.get(
        `/v2/dms/documents/document/${path}`
      );
      commit("SET_DOCUMENT", { ...data, __path: path });
      return data;
    },

    fetchDocumentRaw(b, { path }) {
      const headers = { Accept: "application/pdoc" };
      return http.get(`/v2/dms/documents/document/${path}`, { headers });
    },

    async updateDocument(
      { commit },
      { path, variant, version, content, sources, fields }
    ) {
      const { data } = await http.patch(`/v2/dms/documents/document`, {
        path,
        variant,
        version,
        content,
        sources,
        fields,
      });
      const { version: newVersion } = data;
      commit("UPDATE_DOCUMENT", { path, variant, newVersion, content, sources, fields });
      return data;
    },

    async deleteDocument({ commit, state }, { path }) {
      const { data } = await http.delete(
        `/v2/dms/documents/document/${path}`
      );
      const { status } = data;
      commit("REMOVE_DOCUMENT", path);
      return data;
    },

    async fetchDocuments({ commit, state }, { searchText, recursive }) {
      const { data } = await http.get(`/v2/dms/documents/list`, {
        params: { search: searchText, recursive },
      });
      commit("SET_DOCUMENTS", data);
      return data;
    },

    async fetchDeletedDocuments({ commit, state }, { searchText, recursive }) {
      const { data } = await http.get(
        `/v2/dms/documents/list/__deleted__`,
        {
          params: { search: searchText, recursive },
        }
      );
      commit("SET_DELETED_DOCUMENTS", data);
      return data;
    },

    async restoreDocument({ commit, state }, { path, newPath }) {
      const { data } = await http.post(`/v2/dms/documents/restore`, {
        path,
        newPath,
      });
      commit("RESTORE_DOCUMENT", { path, newPath });
      return data;
    },

    async createComment(
      { commit, state },
      { path, content, variant, from, to }
    ) {
      const { data } = await http.post(
        `/v2/dms/documents/document/comment`,
        { path, content, variant, from, to }
      );
      const { status } = data;
      commit("ADD_COMMENT", { path, content, variant, from, to });
      return data;
    },
  },

  mutations: {
    SET_LIBRARY_UUID(state, uuid) {
      state.library = uuid;
    },

    CLEAN_DOCUMENTS(state) {
      Vue.set(state, "items", []);
    },
    SET_DOCUMENTS(state, docs) {
      Vue.set(state, "items", docs || []);
    },
    SET_DELETED_DOCUMENTS(state, docs) {
      Vue.set(state, "deleted", docs || []);
    },
    SET_DOCUMENT(state, doc) {
      state.document = doc;
    },

    ADD_DOCUMENT(state, doc) {
      state.items.push(doc);
    },

    UPDATE_DOCUMENT(state, { path, variant, newVersion, content, sources, fields }) {
      if (state.document.__path === path) {
        const existingVariant = state.document.variants.find(
          (v) => v.name === variant
        );
        if (existingVariant) {
          existingVariant.version = newVersion;
          existingVariant.content = content;
          existingVariant.sources = sources;
          existingVariant.fields = fields;
        } else {
          state.document.variants.push({
            version: newVersion,
            content,
            sources,
            fields,
            name: variant,
          });
        }
      }
    },

    REMOVE_DOCUMENT(state, path) {
      state.items = state.items.filter((d) => d.name !== path);
    },

    RESTORE_DOCUMENT(state, { path, newPath }) {
      state.deleted = state.deleted.filter((d) => d.name !== path);
      state.items.push({ name: newPath });
    },

    ADD_COMMENT(state, { path, content, variant, from, to }) {
      if (state.document.__path === path) {
        if (variant) {
          const existingVariant = state.document.variants.find(
            (v) => v.name === variant
          );
          if (existingVariant) {
            if (!existingVariant.comments) existingVariant.comments = [];
            existingVariant.comments.push({
              content,
              from,
              to,
            });
          } else {
            // error
          }
        } else {
          if (!state.document.comments) state.document.comments = [];
          state.document.comments.push({
            content,
          });
        }
      }
    },
  },

  getters: {
    library: (s) => s.library,
    documents: (s) => s.items,
    document: (s) => s.document,
    deletedDocuments: (s) => s.deleted,
    getDocumentByID: (s) => (path) =>
      s.items.find((d) => d.name === path) || null,
    documentsByDirPath: (s) => (path) => uniqBy(state.items[path]),
  },

  namespaced: true,
};

export default documents;
